/**
 * Live-ticking countdown for time-boxed sales.
 *
 * Renders "ELITE SALE ENDS IN: HH:MM:SS" as a dark-navy strip with gold
 * uppercase text. Ticks every 1s via a mounted interval. When the timer
 * hits zero, swaps to "SALE ENDED" for one render cycle then unmounts
 * (caller can drop the component when the sale is no longer active).
 *
 * Only renders when `expiresAt` is a valid ISO-8601 timestamp in the
 * future — pass any falsy value / past date and it returns null so callers
 * don't have to guard.
 */
import { useEffect, useState, useMemo } from "react";
import { Clock } from "lucide-react";

const pad = (n) => String(Math.max(0, Math.floor(n))).padStart(2, "0");

const formatDelta = (ms) => {
  if (ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  // For sales longer than 24h we still render as HH — auditors + affiliates
  // find "72:15:03" more useful than "3d 0:15:03" for headline campaigns.
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

export const TimeLeft = ({ expiresAt, label = "ELITE SALE ENDS IN" }) => {
  // Parse once — expiresAt string doesn't change during the sale window.
  const target = useMemo(() => {
    if (!expiresAt) return null;
    const d = new Date(expiresAt);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }, [expiresAt]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return undefined;
    // Align to the next whole second so timers across the page tick together.
    const drift = 1000 - (Date.now() % 1000);
    const initial = setTimeout(() => setNow(Date.now()), drift);
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearTimeout(initial); clearInterval(iv); };
  }, [target]);

  if (!target) return null;

  const remaining = target - now;
  const time = formatDelta(remaining);

  // Sale expired → hide entirely so the sale layer's fallback (base price)
  // becomes the visual truth. Callers listening for on_sale=false will also
  // drop the badge naturally on the next products refresh.
  if (!time) return null;

  // Under 1 hour left → subtle urgency accent (red gold blend).
  const urgent = remaining < 60 * 60 * 1000;

  return (
    <div
      data-testid="sale-timer"
      className={`w-full bg-[#0A192F] ${urgent ? "text-[#F5C542]" : "text-[#D4AF37]"} px-3 py-2 flex items-center justify-center gap-2 font-accent uppercase tracking-[0.15em] text-[10px] sm:text-[11px] border-y ${urgent ? "border-[#F5C542]/50 animate-pulse" : "border-[#D4AF37]/30"}`}
      aria-live="polite"
    >
      <Clock size={12} className="opacity-80" />
      <span className="font-semibold">{label}:</span>
      <span data-testid="sale-timer-value" className="font-mono font-bold tracking-widest">
        {time}
      </span>
    </div>
  );
};
